import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe, formatDate } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../components/modal-escape-guard/modal-escape-guard.directive';
import { Observable, Subject, Subscription, catchError, from, map, mergeMap, of, take } from 'rxjs';
import { PersonSelectComponent, PersonOption } from '../../components/person-select/person-select.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { SuccessBannerComponent } from '../../components/success-banner/success-banner.component';
import { ProjektArchiviertHinweisComponent } from '../../components/projekt-archiviert-hinweis/projekt-archiviert-hinweis.component';
import { DefectService } from '../../services/defect.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { TestRunService } from '../../services/test-run.service';
import { Defect, DefectEvidence, DefectSeverity, DefectStatus } from '../../models/defect.model';
import { ProjectMemberDTO } from '../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { saveBlob } from '../../shared/file-download';
import { formatFileSize } from '../../shared/file-size';
import { serverMessage } from '../../shared/http/server-message';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import {
  DEFECT_SEVERITIES,
  DefectStatusOption,
  defectSeverityTone,
  defectStatusTone,
  statusOptions,
} from './defect-status';
import { DefectChanges, DefectDraft, buildMissing, draftChanges, draftOf } from './defect-draft';

type LoadState = 'loading' | 'ok' | 'notFound' | 'error';

interface PassedRetestRef {
  testRunId: string | null;
  testRunKey: string | null;
}

/** Zusatz im Banner nach einer Aufforderung zum Nachtest (page-fehler.jsx:302). */
interface RetestNotice {
  name: string;
  build: string;
}

const CONFLICT_KEYS: Record<string, string> = {
  DEFECT_NO_PASSED_RETEST: 'defect.detail.error.noPassedRetest',
  DEFECT_VERSION_CONFLICT: 'defect.detail.error.versionConflict',
  DEFECT_INVALID_TRANSITION: 'defect.detail.error.invalidTransition',
};

// SVG bleibt bewusst außen vor: Es kann Skripte enthalten und bekommt nur ein Icon.
const PREVIEW_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

@Component({
  selector: 'app-defect-detail',
  imports: [
    TranslatePipe,
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    MatCheckbox,
    MatIcon,
    MatProgressSpinner,
    Modal,
    ModalEscapeGuardDirective,
    PersonSelectComponent,
    AvatarComponent,
    SuccessBannerComponent,
    ProjektArchiviertHinweisComponent,
  ],
  templateUrl: './defect-detail.component.html',
  styleUrl: './defect-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefectDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly defectService = inject(DefectService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly testRunService = inject(TestRunService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deactivateConfirm$ = new Subject<boolean>();
  private request?: Subscription;

  private readonly projectRole = inject(ProjectRoleService);
  readonly isReadOnly = this.projectRole.isReadOnly;
  readonly isArchived = this.projectRole.isArchived;
  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly defectId: string = this.route.snapshot.params['defectId'];
  readonly severities = DEFECT_SEVERITIES;
  readonly severityTone = defectSeverityTone;
  readonly statusTone = defectStatusTone;

  readonly state = signal<LoadState>('loading');
  readonly defect = signal<Defect | null>(null);
  readonly draft = signal<DefectDraft | null>(null);
  readonly saving = signal(false);
  readonly saved = signal(false);
  readonly retestNotice = signal<RetestNotice | null>(null);
  readonly buildTouched = signal(false);
  readonly startingRetest = signal(false);
  readonly errorText = signal<string | null>(null);
  readonly versionConflict = signal(false);
  readonly showDeactivateConfirm = signal(false);
  private readonly members = signal<ProjectMemberDTO[]>([]);
  /** Vorschau-Adresse je Nachweis; `null`, solange sie lädt oder wenn sie nicht ladbar war. */
  readonly previews = signal<Record<string, string | null>>({});

  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.members()));
  readonly statusOptions = computed<DefectStatusOption[]>(() => {
    const current = this.defect();
    return current ? statusOptions(current.status, current.passedRetest) : [];
  });
  readonly dirty = computed(() => {
    const current = this.defect();
    const draft = this.draft();
    return !!current && !!draft && Object.keys(draftChanges(current, draft)).length > 0;
  });
  /**
   * Bestandener Nachtest für die Fundort-Karte: `verifiedIn`, sonst bei „Behoben“ `passedRetest`,
   * weil der Nachtest schon vor dem Verifizieren bestanden sein kann.
   */
  readonly retest = computed<PassedRetestRef | null>(() => {
    const current = this.defect();
    if (!current) return null;
    if (current.verifiedIn) return current.verifiedIn;
    return current.status === 'FIXED' ? current.passedRetest : null;
  });

  /** Nachtest offen: gespeichert „Behoben“ und noch kein bestandener Nachtest (page-fehler.jsx:262). */
  readonly retestOpen = computed(() => this.defect()?.status === 'FIXED' && !this.retest());
  readonly buildInvalid = computed(() => {
    const draft = this.draft();
    return this.buildTouched() && !!draft && buildMissing(draft);
  });
  /** Ohne Fundort-Durchlauf gibt es keine Grundlage für einen Retest (Annahme 21). */
  readonly canStartRetest = computed(
    () => !this.isReadOnly() && !!this.defect()?.foundIn?.testRunId && !!this.defect()?.foundIn?.executionId,
  );

  readonly assigneeControl = new FormControl<string | null>(null);
  readonly retestByControl = new FormControl<string | null>(null);

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.destroyRef.onDestroy(() => this.releasePreviews(() => false));
  }

  ngOnInit(): void {
    this.memberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => this.members.set(members));
    this.assigneeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((assigneeUserId) => this.change({ assigneeUserId }));
    this.retestByControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((retestByUserId) => this.change({ retestByUserId }));
    this.load();
  }

  load(): void {
    this.request?.unsubscribe();
    this.state.set('loading');
    this.errorText.set(null);
    this.versionConflict.set(false);
    this.saved.set(false);
    this.retestNotice.set(null);
    this.request = this.defectService
      .get(this.projectId, this.defectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (defect) => {
          this.show(defect);
          this.state.set('ok');
        },
        error: (err: unknown) =>
          this.state.set(err instanceof HttpErrorResponse && err.status === 404 ? 'notFound' : 'error'),
      });
  }

  changeStatus(select: HTMLSelectElement): void {
    this.change({ status: select.value as DefectStatus });
  }

  changeSeverity(select: HTMLSelectElement): void {
    this.change({ severity: select.value as DefectSeverity });
  }

  changeMarkedBlocking(markedBlocking: boolean): void {
    this.change({ markedBlocking });
  }

  changeFixedInBuild(input: HTMLInputElement): void {
    this.change({ fixedInBuild: input.value });
  }

  touchBuild(): void {
    this.buildTouched.set(true);
  }

  changeRequestRetest(requestRetest: boolean): void {
    this.change({ requestRetest });
  }

  /** „Nachtest starten“: Retest-Durchlauf des Fundort-Laufs, danach der Runner (Annahme 21). */
  startRetest(): void {
    const runId = this.defect()?.foundIn?.testRunId;
    const executionId = this.defect()?.foundIn?.executionId;
    if (!runId || !executionId || this.startingRetest()) return;
    this.startingRetest.set(true);
    const executions = ['/projects', this.projectId, 'test-runs', runId, 'executions'];
    this.testRunService
      .startExecution(this.projectId, runId, undefined, executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (started) => {
          this.startingRetest.set(false);
          this.router.navigate(
            started.nextAction === 'TRIGGER_PIPELINE' ? executions : [...executions, started.executionId],
          );
        },
        error: (err: unknown) => {
          this.startingRetest.set(false);
          this.router.navigate(executions);
          this.snackBar.open(
            serverMessage(err) ?? this.translate.instant('defect.detail.retest.startError'),
            undefined,
            { duration: 4000 },
          );
        },
      });
  }

  discard(): void {
    const current = this.defect();
    if (current) this.show(current);
  }

  save(): void {
    const current = this.defect();
    const draft = this.draft();
    if (!current || !draft || !this.dirty()) return;
    if (buildMissing(draft)) {
      this.buildTouched.set(true);
      return;
    }
    const changes = draftChanges(current, draft);
    this.setSaving(true);
    this.errorText.set(null);
    this.versionConflict.set(false);
    this.defectService
      .update(this.projectId, this.defectId, { ...changes, version: current.version })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (defect) => {
          this.show(defect);
          this.saved.set(true);
          this.retestNotice.set(this.noticeFor(changes, defect));
          this.setSaving(false);
        },
        error: (err: unknown) => {
          this.errorText.set(this.updateErrorText(err));
          this.setSaving(false);
        },
      });
  }

  hasUnsavedChanges(): boolean {
    return this.dirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  download(evidence: DefectEvidence): void {
    this.errorText.set(null);
    this.testRunService
      .downloadStepResultAttachment(this.projectId, evidence.stepResultId, evidence.attachmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => saveBlob(blob, evidence.originalFilename),
        error: () => this.errorText.set(this.translate.instant('defect.detail.error.downloadFailed')),
      });
  }

  /** „Behoben in Build … · benachrichtigt am …“; fehlende Teile entfallen (Bestand, Annahme 24). */
  retestMeta(defect: Defect): string | null {
    const parts: string[] = [];
    const build = defect.retest?.fixedInBuild;
    const requestedAt = defect.retest?.requestedAt;
    if (build) parts.push(this.translate.instant('defect.detail.retest.build', { build }));
    if (requestedAt) {
      const date = formatDate(requestedAt, 'dd.MM.yyyy', 'en-US');
      parts.push(this.translate.instant('defect.detail.retest.notifiedAt', { date }));
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  isImage(evidence: DefectEvidence): boolean {
    const type = evidence.contentType?.split(';')[0].trim().toLowerCase();
    return !!type && PREVIEW_TYPES.has(type);
  }

  fileSize(evidence: DefectEvidence): string {
    return formatFileSize(evidence.sizeBytes, this.translate.getCurrentLang() || 'de');
  }

  private change(patch: Partial<DefectDraft>): void {
    const draft = this.draft();
    if (!draft) return;
    this.draft.set({ ...draft, ...patch });
    this.saved.set(false);
    this.retestNotice.set(null);
  }

  private show(defect: Defect): void {
    const draft = draftOf(defect);
    this.defect.set(defect);
    this.draft.set(draft);
    this.buildTouched.set(false);
    this.assigneeControl.setValue(draft.assigneeUserId, { emitEvent: false });
    this.retestByControl.setValue(draft.retestByUserId, { emitEvent: false });
    this.loadPreviews(defect.evidence);
  }

  private noticeFor(sent: DefectChanges, saved: Defect): RetestNotice | null {
    if (!sent.requestRetest || !sent.retestByUserId) return null;
    const name =
      saved.retest?.retestBy?.name ??
      this.personOptions().find((option) => option.id === sent.retestByUserId)?.displayName ??
      null;
    const build = saved.retest?.fixedInBuild ?? sent.fixedInBuild ?? null;
    return name && build ? { name, build } : null;
  }

  private loadPreviews(evidence: DefectEvidence[]): void {
    const current = new Set(evidence.map((file) => file.attachmentId));
    this.releasePreviews((attachmentId) => current.has(attachmentId));
    const missing = evidence.filter((file) => this.isImage(file) && !(file.attachmentId in this.previews()));
    if (missing.length === 0) return;
    this.previews.update((previews) => ({
      ...previews,
      ...Object.fromEntries(missing.map((file) => [file.attachmentId, null])),
    }));
    from(missing)
      .pipe(
        mergeMap((file) =>
          this.testRunService.downloadStepResultAttachment(this.projectId, file.stepResultId, file.attachmentId).pipe(
            map((blob): string | null => URL.createObjectURL(blob)),
            catchError(() => of(null)),
            map((url) => [file.attachmentId, url] as const),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([attachmentId, url]) => this.previews.update((previews) => ({ ...previews, [attachmentId]: url })));
  }

  private releasePreviews(keep: (attachmentId: string) => boolean): void {
    const kept: Record<string, string | null> = {};
    for (const [attachmentId, url] of Object.entries(this.previews())) {
      if (keep(attachmentId)) {
        kept[attachmentId] = url;
      } else if (url) {
        URL.revokeObjectURL(url);
      }
    }
    this.previews.set(kept);
  }

  private setSaving(saving: boolean): void {
    this.saving.set(saving);
    for (const control of [this.assigneeControl, this.retestByControl]) {
      if (saving) control.disable({ emitEvent: false });
      else control.enable({ emitEvent: false });
    }
  }

  private updateErrorText(err: unknown): string {
    const code =
      err instanceof HttpErrorResponse && err.status === 409 ? (err.error as { code?: unknown })?.code : null;
    if (typeof code === 'string' && CONFLICT_KEYS[code]) {
      this.versionConflict.set(code === 'DEFECT_VERSION_CONFLICT');
      return this.translate.instant(CONFLICT_KEYS[code]);
    }
    return serverMessage(err) ?? this.translate.instant('defect.detail.error.updateFailed');
  }
}
